import { AlertTriangle, Check, Loader2, ShieldAlert, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useApprovalFacts } from "./useApprovalFacts";

/**
 * The human-in-the-loop gate. Nothing the assistant proposes that changes a
 * campaign, spends money, or touches a client's account executes until this card
 * is answered — the server refuses to run the tool without an approval response.
 *
 * Everything identifying the target is resolved server-side (see
 * useApprovalFacts). The model supplies ids and a reason; it does not supply the
 * names shown here, because a card whose description came from the model could
 * be made to disagree with the ids underneath it.
 */
export interface ToolApprovalCardProps {
  toolName: string;
  input: Record<string, unknown>;
  projectId?: string;
  onRespond: (approved: boolean) => void;
  /** Already answered, or no longer answerable — show the outcome, not buttons. */
  decided?: "approved" | "denied" | "expired";
}

/** Fields safe to show verbatim: written by the model but purely descriptive. */
const MODEL_TEXT_FIELDS = ["reason"] as const;

/** Action parameters worth surfacing, rendered as-is (no name resolution). */
const PARAM_FIELDS = [
  "platform",
  "periodStart",
  "periodEnd",
  "deliveryEmail",
  "reportType",
  "language",
] as const;

/** Fields already covered by the server-verified rows above. */
const HANDLED_FIELDS = new Set<string>([
  ...MODEL_TEXT_FIELDS,
  ...PARAM_FIELDS,
  "projectId",
  "campaignId",
  "alertId",
]);

function formatFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export function ToolApprovalCard({
  toolName,
  input,
  projectId,
  onRespond,
  decided,
}: ToolApprovalCardProps) {
  const { t } = useTranslation();
  const { facts, loading } = useApprovalFacts(toolName, input, projectId);

  const label = t(`assistant.approval.tools.${toolName}`, { defaultValue: toolName });

  const extra = Object.entries(input).filter(([key]) => !HANDLED_FIELDS.has(key));
  const hasUnverified = (facts?.unverified?.length ?? 0) > 0;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">{label}</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            {t("assistant.approval.subtitle")}
          </p>

          <dl className="mt-2.5 space-y-1">
            {/* Server-verified target. */}
            <Row
              label={t("assistant.approval.fields.client")}
              value={
                loading
                  ? null
                  : (facts?.projectName ?? t("assistant.approval.unverifiedValue"))
              }
              loading={loading}
              warn={!loading && !facts?.projectName}
            />

            {typeof input.campaignId === "string" && (
              <Row
                label={t("assistant.approval.fields.campaign")}
                value={
                  loading
                    ? null
                    : (facts?.campaignName ?? t("assistant.approval.unverifiedValue"))
                }
                loading={loading}
                warn={!loading && !facts?.campaignName}
              />
            )}

            {typeof input.alertId === "string" && (
              <Row
                label={t("assistant.approval.fields.alert")}
                value={
                  loading
                    ? null
                    : (facts?.alertTitle ?? t("assistant.approval.unverifiedValue"))
                }
                loading={loading}
                warn={!loading && !facts?.alertTitle}
              />
            )}

            {PARAM_FIELDS.filter(
              (f) => input[f] !== undefined && input[f] !== null && input[f] !== "",
            ).map((field) => (
              <Row
                key={field}
                label={t(`assistant.approval.fields.${field}`, {
                  defaultValue: formatFieldName(field),
                })}
                value={String(input[field])}
              />
            ))}

            {/* Anything else the model sent still changes state, so it has to be
                visible — approving a card that hides half the payload is not
                informed consent. */}
            {extra.map(([key, value]) => (
              <Row
                key={key}
                label={formatFieldName(key)}
                value={typeof value === "object" ? JSON.stringify(value) : String(value)}
              />
            ))}
          </dl>

          {typeof input.reason === "string" && input.reason && (
            <p className="mt-2 text-xs text-neutral-600 italic">
              {String(input.reason)}
            </p>
          )}

          {hasUnverified && !loading && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
              {t("assistant.approval.unverifiedWarning")}
            </p>
          )}

          {decided ? (
            <p
              className={`mt-3 text-xs font-semibold ${
                decided === "approved" ? "text-emerald-600" : "text-neutral-500"
              }`}
            >
              {t(`assistant.approval.${decided}`)}
            </p>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => onRespond(true)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-neutral-800 transition-colors cursor-pointer disabled:opacity-40"
              >
                <Check className="w-3.5 h-3.5" />
                {t("assistant.approval.approve")}
              </button>
              <button
                onClick={() => onRespond(false)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 transition-colors cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
                {t("assistant.approval.deny")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  loading,
  warn,
}: {
  label: string;
  value: string | null;
  loading?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="flex gap-2 text-xs">
      <dt className="text-neutral-500 flex-shrink-0">{label}</dt>
      <dd
        className={`font-medium break-words min-w-0 ${
          warn ? "text-amber-700" : "text-neutral-800"
        }`}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : value}
      </dd>
    </div>
  );
}
